import { FilterRule } from '../types'

// Filter rule type mapping (same as React)
interface FilterRuleType {
  id: number
  filtervar: string
  isnull_filtervar?: string
  multi: boolean
  datatype?: string
}

// Negative null filter value constant (same as React)
const NEGATIVE_NULL_FILTER_VALUE = -1

// Simplified mapping of filter rule types (matching React constants)
const FILTER_RULE_TYPES: FilterRuleType[] = [
  // Text filters
  { id: 0, filtervar: 'title__icontains', multi: false }, // FILTER_TITLE
  { id: 19, filtervar: 'title_content', multi: false }, // FILTER_TITLE_CONTENT
  { id: 2, filtervar: 'archive_serial_number', multi: false }, // FILTER_ASN
  { id: 18, filtervar: 'archive_serial_number__isnull', multi: false, datatype: 'boolean' }, // FILTER_ASN_ISNULL
  { id: 23, filtervar: 'archive_serial_number__gt', multi: false }, // FILTER_ASN_GT
  { id: 24, filtervar: 'archive_serial_number__lt', multi: false }, // FILTER_ASN_LT
  { id: 20, filtervar: 'query', multi: false }, // FILTER_FULLTEXT_QUERY
  { id: 21, filtervar: 'more_like_id', multi: false }, // FILTER_FULLTEXT_MORELIKE
  { id: 36, filtervar: 'custom_fields__icontains', multi: false }, // FILTER_CUSTOM_FIELDS_TEXT
  { id: 47, filtervar: 'mime_type', multi: false }, // FILTER_MIME_TYPE

  // Tags
  { id: 6, filtervar: 'tags__id__all', multi: true },
  { id: 7, filtervar: 'is_tagged', multi: false, datatype: 'boolean' },
  { id: 17, filtervar: 'tags__id__none', multi: true },
  { id: 22, filtervar: 'tags__id__in', multi: true },

  // Correspondent
  { id: 3, filtervar: 'correspondent__id', isnull_filtervar: 'correspondent__isnull', multi: false }, // FILTER_CORRESPONDENT
  { id: 26, filtervar: 'correspondent__id__in', multi: true },
  { id: 27, filtervar: 'correspondent__id__none', multi: true },

  // Document Type
  { id: 4, filtervar: 'document_type__id', isnull_filtervar: 'document_type__isnull', multi: false }, // FILTER_DOCUMENT_TYPE
  { id: 28, filtervar: 'document_type__id__in', multi: true },
  { id: 29, filtervar: 'document_type__id__none', multi: true },

  // Workflow Status
  { id: 48, filtervar: 'workflow_status__id', isnull_filtervar: 'workflow_status__isnull', multi: false }, // FILTER_WORKFLOW_STATUS
  { id: 49, filtervar: 'workflow_status__id__in', multi: true },
  { id: 50, filtervar: 'workflow_status__id__none', multi: true },

  // Storage Path
  { id: 25, filtervar: 'storage_path__id', isnull_filtervar: 'storage_path__isnull', multi: false }, // FILTER_STORAGE_PATH
  { id: 30, filtervar: 'storage_path__id__in', multi: true },
  { id: 31, filtervar: 'storage_path__id__none', multi: true },

  // Dates
  { id: 43, filtervar: 'created__date__lte', multi: false },
  { id: 44, filtervar: 'created__date__gte', multi: false },
  { id: 45, filtervar: 'added__date__lte', multi: false },
  { id: 46, filtervar: 'added__date__gte', multi: false },

  // Permissions
  { id: 32, filtervar: 'owner__id', multi: false },
  { id: 33, filtervar: 'owner__id__in', multi: true },
  { id: 34, filtervar: 'owner__isnull', multi: false, datatype: 'boolean' },
  { id: 35, filtervar: 'owner__id__none', multi: true },
  { id: 37, filtervar: 'shared_by__id', multi: true },

  // Custom field queries
  { id: 42, filtervar: 'custom_field_query', multi: false },
]

const SORT_FIELD_PARAMETER = 'sort'
const SORT_REVERSE_PARAMETER = 'reverse'
const PAGE_PARAMETER = 'page'

/**
 * Convert filter rules to URL query parameters
 * This follows React's implementation exactly
 */
export function queryParamsFromFilterRules(filterRules: FilterRule[]): Record<string, string | number> {
  if (!filterRules || filterRules.length === 0) {
    return {}
  }

  const params: Record<string, string | number> = {}

  for (const rule of filterRules) {
    const ruleType = FILTER_RULE_TYPES.find((t) => t.id === rule.rule_type)
    if (!ruleType) {
      continue
    }

    if (ruleType.isnull_filtervar && rule.value === null) {
      params[ruleType.isnull_filtervar] = 1
    } else if (ruleType.isnull_filtervar && rule.value === NEGATIVE_NULL_FILTER_VALUE.toString()) {
      params[ruleType.isnull_filtervar] = 0
    } else if (ruleType.multi) {
      // For multi-value parameters, concatenate with comma
      const currentValue = params[ruleType.filtervar]
      const stringValue = String(rule.value)
      params[ruleType.filtervar] = currentValue ? String(currentValue) + ',' + stringValue : stringValue
    } else {
      if (ruleType.datatype === 'boolean') {
        params[ruleType.filtervar] = rule.value === 'true' || rule.value === '1' ? 1 : 0
      } else {
        params[ruleType.filtervar] = typeof rule.value === 'number' ? rule.value : String(rule.value)
      }
    }
  }

  return params
}

/**
 * Convert URL query parameters to filter rules
 */
export function filterRulesFromQueryParams(queryParams: URLSearchParams): FilterRule[] {
  const allFilterRuleQueryParams: string[] = FILTER_RULE_TYPES.map((rt) => rt.filtervar).concat(
    FILTER_RULE_TYPES.map((rt) => rt.isnull_filtervar).filter((rt) => rt !== undefined) as string[]
  )

  let filterRulesFromQueryParams: FilterRule[] = []

  allFilterRuleQueryParams
    .filter((frqp) => queryParams.has(frqp))
    .forEach((filterQueryParamName) => {
      const rule_type = FILTER_RULE_TYPES.find((rt) => rt.filtervar === filterQueryParamName || rt.isnull_filtervar === filterQueryParamName)

      if (!rule_type) {
        return
      }

      const isNullRuleType = rule_type.isnull_filtervar === filterQueryParamName
      // React uses null, but converts to string - use NEGATIVE_NULL_FILTER_VALUE.toString()
      const nullRuleValue =
        queryParams.get(filterQueryParamName) === '1'
          ? NEGATIVE_NULL_FILTER_VALUE.toString() // null -> "-1" (representing null)
          : NEGATIVE_NULL_FILTER_VALUE.toString() // not null -> "-1" as well (representing not null)

      const valueURIComponent: string = queryParams.get(filterQueryParamName)!
      const filterQueryParamValues: string[] = rule_type.multi ? valueURIComponent.split(',') : [valueURIComponent]

      const newRules: FilterRule[] = filterQueryParamValues.map((val) => {
        if (rule_type.datatype === 'boolean') val = val.replace('1', 'true').replace('0', 'false')
        const finalValue = isNullRuleType ? nullRuleValue : val
        return {
          rule_type: rule_type.id,
          value: finalValue as string | number | boolean,
        }
      })

      filterRulesFromQueryParams = filterRulesFromQueryParams.concat(newRules)
    })

  return filterRulesFromQueryParams
}

/**
 * Convert view state to URL query parameters
 */
export function paramsFromViewState(
  filterRules: FilterRule[],
  sortField: string,
  sortReverse: boolean,
  currentPage: number,
  pageOnly: boolean = false
): Record<string, string | number | undefined> {
  const params: Record<string, string | number | undefined> = pageOnly ? {} : queryParamsFromFilterRules(filterRules)

  if (!pageOnly) {
    params[SORT_FIELD_PARAMETER] = sortField
    params[SORT_REVERSE_PARAMETER] = sortReverse ? 1 : undefined
  }

  if (!pageOnly || currentPage !== 1) {
    params[PAGE_PARAMETER] = currentPage
  }

  return params
}

const ASSIGNED_TO_ME_PARAMETER = 'assigned_to_me'

/**
 * Convert URL query parameters to view state
 */
export function paramsToViewState(queryParams: URLSearchParams): {
  filterRules: FilterRule[]
  sortField: string
  sortReverse: boolean
  currentPage: number
  assignedToMe: boolean
} {
  const filterRules = filterRulesFromQueryParams(queryParams)
  const sortField = queryParams.get(SORT_FIELD_PARAMETER) || 'created'
  const sortReverse = queryParams.get(SORT_REVERSE_PARAMETER) === '1'
  const currentPage = parseInt(queryParams.get(PAGE_PARAMETER) || '1', 10)
  const assignedToMe = queryParams.get(ASSIGNED_TO_ME_PARAMETER) === 'true'

  return {
    filterRules,
    sortField,
    sortReverse,
    currentPage,
    assignedToMe,
  }
}
